import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useSelector } from 'react-redux';
import {
  useTrackTransferMutation,
  usePrepareTransactionMutation,
  useBroadcastAndTrackMutation,
} from '../../../../../redux/api/authorized/gifts.js';
import { useLocation } from 'react-router-dom';
import { PHONE_MEDIA_QUERY } from '../../../../../common/responsive.js';
import GiftCard from '../../../../../components/GiftCard/index.jsx';
import { useSolana } from '../../../../../hooks/useSolana.js';
import { useGetSwapQuoteQuery } from '../../../../../redux/api/authorized/swap';
import { fromTokenAmount } from '../../../../../hooks/useSolana';
import ConfirmationSkeleton from './skeleton.jsx';
import Information from '../../../../../components/Information';
import StripeEmbed from '../../../../../components/StripeEmbed';
import {
  selectTokenByNetworkAndAddress,
  selectTokenByNetworkAndSymbol,
} from '../../../../../redux/slices/tokenSlice';
import { selectWalletTokenBalance } from '../../../../../redux/slices/walletSlice';
import { selectTokenBySymbol } from '../../../../../redux/slices/tokenSlice';
import {
  EVENT_GIFTCARD_SENT,
  sendEvent,
} from '../../../../../common/analytics.js';
import { formatTokenPrice } from '../../../../../common/currency.js';
import ScrollRestore from '../../../../../components/ScrollRestore/index.tsx';
import { ParagraphLargeText } from '../../../../../components/Texts/index.tsx';
import Steps from '../../../../../components/Steps/index.jsx';
import { useGiftForm } from '../../../../../components/GiftFormContext/index.jsx';
import Button from '../../../../../components/Button/index.tsx';
import SendingStatus from '../../../../Gifts/SendingStatus/index.jsx';
import { hapticsImpactMedium } from '../../../../../common/haptic.js';

const GiftCardContainer = styled.div`
  width: 417px;
  margin-left: auto;
  margin-right: auto;
  height: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100%;
  }
`;

const DemoContainer = styled.div`
  width: 100%;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-end;
  flex: 1;

  .stripe-onramp-wrapper {
    margin: 0;
    padding: 0;
  }
`;

const Line = styled.div`
  margin-top: 40px;
  margin-bottom: 40px;
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.border.primary};
  @media ${PHONE_MEDIA_QUERY} {
    margin-top: 20px;
    margin-bottom: 20px;
  }
`;

const Message = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.status.error.primary};
  text-align: center;
`;

const SummaryTitle = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

const SummaryDescription = styled.p`
  font-size: 16px;
  font-weight: 400;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 12px;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const ContentRow = styled.div`
  display: flex;
  width: 100%;
  gap: 48px;
  color: ${({ theme }) => theme.text.regular};
  flex-direction: column;
`;

const MemoizedStripeEmbed = React.memo(
  ({ destinationAmount, onSuccess, onError }) => (
    <StripeEmbed
      destinationAmount={destinationAmount}
      onSuccess={onSuccess}
      onError={onError}
    />
  ),
);

const Confirmation = () => {
  const theme = useTheme();
  const location = useLocation();
  const {
    feedback,
    gift,
    payingWith,
    token: tokenAddress,
    network,
    setFeedback,
    sendingStatus,
    setSendingStatus,
    claimUrl,
    setClaimUrl,
  } = useGiftForm();

  const token = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, network, tokenAddress),
  );

  const baseToken = payingWith;

  const [waitingForUSDC, setWaitingForUSDC] = useState(false);
  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, 'solana', 'USDC'),
  );

  // Track the original gift type to preserve it even after sending
  const originalGiftTypeRef = useRef(null);

  const baseTokenObj = useSelector((state) =>
    selectTokenBySymbol(state, 'solana', baseToken?.symbol || ''),
  );
  const embeddedBalance = useSelector((state) =>
    selectWalletTokenBalance(
      state,
      'solana',
      baseTokenObj?.address || '',
      'embedded',
    ),
  );
  const externalBalance = useSelector((state) =>
    selectWalletTokenBalance(
      state,
      'solana',
      baseTokenObj?.address || '',
      'external',
    ),
  );

  // Track the original gift type when gift changes
  useEffect(() => {
    if (gift && originalGiftTypeRef.current === null) {
      // Determine original gift type based on whether purchaseUsdValue was originally present
      const hadOriginalUsdValue = !!(
        gift?.purchaseUsdValue && parseFloat(gift.purchaseUsdValue) > 0
      );
      originalGiftTypeRef.current = hadOriginalUsdValue ? 'usd' : 'token';
    }
  }, [gift]);

  useEffect(() => {
    if (gift?.claimUrl && !claimUrl) {
      setClaimUrl(gift.claimUrl);
    }
  }, [gift]);

  useEffect(() => {
    if (gift.status !== 'DRAFT') {
      setSendingStatus('success');
    }
  }, [gift]);

  // Checkout mode only works if base token is USD (for now)
  const isCheckout =
    location.pathname.includes('checkout') &&
    baseToken?.address !== usdcToken?.address;

  const isSameToken = gift?.token === gift?.baseToken;

  const totalBaseTokenFeeAmount = fromTokenAmount(
    gift.fee,
    baseToken?.decimals || 6,
  );
  const totalBaseTokenAmount =
    fromTokenAmount(gift.baseTokenAmount, baseToken?.decimals || 6) +
    totalBaseTokenFeeAmount;

  const { data: swapQuote } = useGetSwapQuoteQuery(
    {
      inputMint: gift?.baseToken,
      outputMint: usdcToken?.address,
      amount: (BigInt(gift?.baseTokenAmount) + BigInt(gift?.fee)).toString(), // Use total amount to get USD price of total, then manually calculate fee (easier that way)
      slippageBps: import.meta.env.VITE_DEFAULT_SLIPPAGE || 'auto',
      // TODO: UPDATE THIS IF FEES ARE ADDED BACK
      calculateFees: false,
      isGift: true,
    },
    {
      skip: !gift?.baseToken || !gift?.token || !gift?.baseTokenAmount,
      pollingInterval: 10000,
    },
  );

  let tokenAmount = 0;
  let amountUSD = 0;
  let feeUSD = 0;
  let totalAmountUSD = 0;
  let transferFeeUSD = 0;

  if (isSameToken) {
    totalAmountUSD = fromTokenAmount(
      swapQuote?.quote.outAmount,
      usdcToken?.decimals || 6,
    );
    // We use swapQuote amount as it accounts for the transfer fee, if any
    tokenAmount = fromTokenAmount(
      swapQuote?.amounts?.swapOutputAmount,
      baseToken.decimals,
    );
    feeUSD = fromTokenAmount(
      swapQuote?.feesUSD?.totalFee,
      usdcToken?.decimals || 6,
    );
    amountUSD = fromTokenAmount(
      swapQuote?.amountsUSD?.swapOutputAmount,
      usdcToken?.decimals || 6,
    );
    transferFeeUSD = fromTokenAmount(
      swapQuote?.feesUSD?.transferFee,
      usdcToken?.decimals || 6,
    );
  } else {
    // Base token is USDC if not same token
    tokenAmount = swapQuote?.quote?.outAmount
      ? fromTokenAmount(swapQuote.quote.outAmount, token?.decimals || 9)
      : 0;
    amountUSD = fromTokenAmount(
      swapQuote?.amountsUSD?.swapOutputAmount,
      usdcToken?.decimals || 6,
    );
    feeUSD = fromTokenAmount(
      swapQuote?.feesUSD?.totalFee,
      usdcToken?.decimals || 6,
    );
    totalAmountUSD = amountUSD + feeUSD;
    transferFeeUSD = fromTokenAmount(
      swapQuote?.feesUSD?.transferFee,
      usdcToken?.decimals || 6,
    );
  }

  // Check if this is a token-amount based gift (preserve original type)
  const isTokenAmountGift = originalGiftTypeRef.current === 'token';

  // Format all amounts for display
  const formattedFee = feeUSD.toFixed(2);
  const formattedAmountUSD = formatTokenPrice(
    parseFloat(gift?.purchaseUsdValue) - parseFloat(feeUSD).toFixed(2),
  );
  const formattedTotalAmount = gift?.purchaseUsdValue;
  const formattedTransferFee = transferFeeUSD.toFixed(2);

  // For token-amount based gifts, use the tokenAmount from the gift object
  const giftTokenAmount = fromTokenAmount(
    gift?.tokenAmount || '0',
    token?.decimals || 9,
  );
  const formattedTokenAmount = formatTokenPrice(giftTokenAmount, amountUSD);

  const {
    getTokenBalance,
    parseSolanaError,
    transferToken,
    embeddedWallet,
    externalWallet,
    executeGiftSwap,
    executeGiftHTLCCreation,
    signAndSerializeTransaction,
    refreshTokenBalances,
  } = useSolana();
  const [trackTransfer] = useTrackTransferMutation();
  const [prepareTransaction] = usePrepareTransactionMutation();
  const [broadcastAndTrack] = useBroadcastAndTrackMutation();

  const handleSendNow = async () => {
    try {
      if (gift.network === 'solana') {
        // Check if swap is needed and hasn't been attempted yet
        const needsSwap = gift?.htlcExpiry && gift.baseToken !== gift.token;
        const swapAlreadyAttempted = !!gift.swapTransactionHash;

        // Only check balance and transfer if we haven't done the swap yet
        if (!swapAlreadyAttempted) {
          // First check if combined balance is sufficient
          const combinedBalance = embeddedBalance + externalBalance;
          if (combinedBalance < totalBaseTokenAmount) {
            throw new Error(
              `Insufficient ${baseToken.symbol} balance. Need ${totalBaseTokenAmount} total but have ${embeddedBalance} in embedded wallet and ${externalBalance} in external wallet. Combined balance: ${combinedBalance}`,
            );
          }

          // Then check if we need to transfer funds from external to embedded wallet
          if (embeddedBalance < totalBaseTokenAmount) {
            const amountNeeded = totalBaseTokenAmount - embeddedBalance;

            // Transfer only the difference needed from external to embedded wallet
            const transferResult = await transferToken(
              baseToken.symbol,
              amountNeeded,
              externalWallet,
              embeddedWallet.address,
            );

            if (transferResult.error) {
              throw new Error(
                `Failed to transfer funds: ${transferResult.error}`,
              );
            }
          }
        }

        // Handle different gift types
        const isHTLCGift =
          gift?.htlcExpiry ||
          gift?.htlcAccount ||
          gift?.status?.includes('HTLC');

        if (needsSwap && !swapAlreadyAttempted && isHTLCGift) {
          // Execute swap first for HTLC gifts
          setSendingStatus('sending');
          setFeedback('Preparing swap transaction...');

          const swapResult = await executeGiftSwap({
            giftId: gift.id,
            inputMint: gift.baseToken,
            inputAmount: gift.baseTokenAmount,
            slippageBps: 'auto',
          });

          if (!swapResult) {
            throw new Error('Swap transaction failed. Please try again.');
          }

          setFeedback(
            'Swap completed successfully. Preparing gift transaction...',
          );

          // Proceed with HTLC creation after swap
          const result = await executeGiftHTLCCreation({
            giftId: gift.id,
            outputMint: gift.token,
          });

          if (!result) {
            throw new Error('Gift transaction failed. Please try again.');
          }

          setClaimUrl(result?.gift?.claimUrl);

          sendEvent(EVENT_GIFTCARD_SENT, {
            id: gift.id,
            token: gift.token,
            base_token_amount: gift.baseToken,
            base_token: gift.baseTokenAmount,
            created_date: new Date().toISOString(),
          });

          if (result.transactionId) {
            setSendingStatus('success');
            setFeedback(null);
          }
        } else if (needsSwap && !swapAlreadyAttempted && !isHTLCGift) {
          // For non-HTLС gifts that need swap, use regular prepareTransaction
          setSendingStatus('sending');
          setFeedback('Preparing gift transaction...');

          const { serializedTransaction } = await prepareTransaction(
            gift.id,
          ).unwrap();

          // Sign and broadcast the transaction
          const base64String = await signAndSerializeTransaction(
            serializedTransaction,
          );

          const result = await broadcastAndTrack({
            giftId: gift.id,
            serializedTransaction: base64String,
          }).unwrap();

          if (!result) {
            throw new Error('Gift transaction failed. Please try again.');
          }

          setClaimUrl(result?.gift?.claimUrl);

          sendEvent(EVENT_GIFTCARD_SENT, {
            id: gift.id,
            token: gift.token,
            base_token_amount: gift.baseToken,
            base_token: gift.baseTokenAmount,
            created_date: new Date().toISOString(),
          });

          if (result.transactionId) {
            setSendingStatus('success');
            setFeedback(null);
          }
        } else {
          // No swap needed, proceed directly with gift creation
          setSendingStatus('sending');
          setFeedback('Preparing gift transaction...');

          const result = await executeGiftHTLCCreation({
            giftId: gift.id,
            outputMint: gift.token,
          });

          if (!result) {
            throw new Error('Gift transaction failed. Please try again.');
          }

          setClaimUrl(result?.gift?.claimUrl);

          sendEvent(EVENT_GIFTCARD_SENT, {
            id: gift.id,
            token: gift.token,
            base_token_amount: gift.baseToken,
            base_token: gift.baseTokenAmount,
            created_date: new Date().toISOString(),
          });

          if (result.transactionId) {
            setSendingStatus('success');
            setFeedback(null);
          }
        }
      } else {
        // Handle Ethereum network gifts
        const receiverAddress = gift.receiver.ethereumAddress;
        const transactionRequest = {
          to: receiverAddress,
          value: gift.baseTokenAmount,
          ...transactionRequest,
        };

        const transactionId = await window.ethereum.request({
          method: 'eth_sendTransaction',
          params: [transactionRequest],
        });

        if (transactionId) {
          await trackTransfer({
            giftId: gift.id,
            transactionId,
          }).unwrap();
          setSendingStatus('success');
        }
      }
    } catch (error) {
      setSendingStatus('error');
      const errorMessage = parseSolanaError(error);
      setFeedback(`Error: ${errorMessage}`);
    }
  };

  useEffect(() => {
    if (sendingStatus === 'success') {
      refreshTokenBalances();
      hapticsImpactMedium();
    }
  }, [sendingStatus]);

  useEffect(() => {
    let pollInterval;

    const checkAndSendGift = async () => {
      const { balance: usdcBalance } = await getTokenBalance({
        tokenAddress: baseToken?.address,
        programId: baseToken?.programId,
      });
      if (usdcBalance >= totalAmountUSD) {
        clearInterval(pollInterval);
        setWaitingForUSDC(false);
        await handleSendNow();
      }
    };

    if (waitingForUSDC) {
      setSendingStatus('sending');
      pollInterval = setInterval(checkAndSendGift, 1000);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [waitingForUSDC, totalAmountUSD, baseToken]);

  const handleStripeSuccess = useCallback(() => {
    setWaitingForUSDC(true);
  }, []);

  const handleStripeError = useCallback((error) => {
    const errorMessage =
      typeof error === 'string'
        ? error
        : error?.message || 'An unknown error occurred';
    setFeedback(`Error: ${errorMessage}`);
  }, []);

  if (!gift || !token || !baseToken) {
    return <ConfirmationSkeleton />;
  }

  if (sendingStatus === 'sending' || sendingStatus === 'success') {
    return (
      <>
        <SendingStatus
          giftUrl={gift?.claimUrl || claimUrl}
          email={gift.receiver?.email}
          token={token}
          cardImage={gift.imageUrl}
          isSuccess={sendingStatus === 'success'}
          usdAmount={isTokenAmountGift ? undefined : formattedAmountUSD}
          tokenAmount={isTokenAmountGift ? giftTokenAmount : tokenAmount}
        />
      </>
    );
  }

  return (
    <>
      <ScrollRestore />
      <DemoContainer>
        <ContentRow>
          <GiftCardContainer>
            <GiftCard
              usdAmount={isTokenAmountGift ? undefined : formattedAmountUSD}
              tokenAmount={isTokenAmountGift ? giftTokenAmount : undefined}
              cardImage={gift.imageUrl}
              token={token}
            />
          </GiftCardContainer>
          <FormContainer>
            <SummaryRow>
              <SummaryTitle>
                {formattedTokenAmount} {token.symbol} Gift Card
              </SummaryTitle>
              <SummaryTitle>
                {isTokenAmountGift
                  ? `${formattedTokenAmount} ${token.symbol}`
                  : `$${formattedAmountUSD}`}
              </SummaryTitle>
            </SummaryRow>
            {feeUSD > 0 && (
              <SummaryRow>
                <SummaryDescription>Gifting Fee</SummaryDescription>
                <SummaryDescription>+ ${formattedFee}</SummaryDescription>
              </SummaryRow>
            )}
            {transferFeeUSD > 0 && (
              <SummaryRow>
                <SummaryDescription>Cause Fee</SummaryDescription>
                <SummaryDescription>
                  + ${formattedTransferFee}
                </SummaryDescription>
              </SummaryRow>
            )}
            {gift.receiver?.email && (
              <SummaryRow>
                <SummaryDescription>
                  <span style={{ color: `${theme.text.secondary}` }}>
                    Receiver:
                  </span>{' '}
                  {gift.receiver.email}
                </SummaryDescription>
              </SummaryRow>
            )}
            {gift.message && (
              <SummaryRow style={{ flexDirection: 'column' }}>
                <SummaryDescription>
                  <span style={{ color: `${theme.text.secondary}` }}>
                    Message
                  </span>
                </SummaryDescription>
                <SummaryDescription>{gift.message}</SummaryDescription>
              </SummaryRow>
            )}
            {isCheckout && (
              <SummaryRow>
                <SummaryTitle>Total:</SummaryTitle>
                <SummaryTitle>${formattedTotalAmount}</SummaryTitle>
              </SummaryRow>
            )}
          </FormContainer>
        </ContentRow>
        <Line />
        <ContentRow>
          {isCheckout ? (
            <>
              <Information />
              <FormContainer>
                <MemoizedStripeEmbed
                  destinationAmount={totalAmountUSD}
                  onSuccess={handleStripeSuccess}
                  onError={handleStripeError}
                />
                {feedback && <Message>{feedback}</Message>}
              </FormContainer>
            </>
          ) : (
            <>
              <FormContainer>
                <div style={{ display: 'flex', gap: 20, width: '100%' }}>
                  <Button fullWidth onClick={handleSendNow}>
                    Purchase Gift
                  </Button>
                </div>
                {feedback && <Message>{feedback}</Message>}
              </FormContainer>
            </>
          )}
        </ContentRow>
      </DemoContainer>
    </>
  );
};

export default Confirmation;
